import { Controller } from "stimulus";
import tomSelect from 'tom-select';

export default class FormController extends Controller {

    connect() {
        this.connectedAt = Date.now();

        if (window.supercast) {
            window.supercast.trigger('pageload')
        }

        this.refreshSummary();

        // Re-check on a timer so a page left open re-hides the field as its publish time drifts
        // under the floor. Thirty seconds is well inside the five-minute floor.
        this.summaryTimer = setInterval(() => this.refreshSummary(), 30_000);
    }

    disconnect() {
        if (this.summaryTimer) clearInterval(this.summaryTimer);
    }

    // Compares like with like: the server-rendered data-server-now string is emitted in the same
    // zone as the published_at input's value, so both are shifted by the same browser offset when
    // parsed as local and the offset cancels. See the timezone note in the plan this shipped with.
    secondsUntilPublish() {
        if (!this.hasPublishAtInputTarget) return null;
        const publishMs = Date.parse(this.publishAtInputTarget.value);
        const anchorMs = Date.parse(this.element.dataset.serverNow);
        if (isNaN(publishMs) || isNaN(anchorMs)) return null;

        return (publishMs - (anchorMs + (Date.now() - this.connectedAt))) / 1000;
    }

    playlistSelectTargetConnected(el) {
        this.playlistTom = new tomSelect(el, {
            create: (input) => {
                return {
                    value: "new:" + input,
                    text: input
                }
            }
        });
    }

    removeTimestamp(e) {
        e.preventDefault();
        e.target.closest("[data-timestamp]").remove();
    }

    emailContentTargetConnected(el) {
        el.addEventListener('trix-change', (e) => {
            this.emailContentAlertTarget.dataset.email = e.target.editor.getDocument().toString();
            this.maybeWarn();
        });

        this.maybeWarn();
    }

    maybeWarn() {
        if (this.emailContentAlertTarget.dataset.description !== this.emailContentAlertTarget.dataset.email) {
            this.emailContentAlertTarget.classList.remove('hidden');
        }
    }

    togglePremiereBanner(event) {
        if (!this.hasPremiereBannerTarget) return;

        this.premiereBannerTarget.classList.toggle("hidden", !event.target.checked);
    }

    // Re-run whenever a summary line connects, so a Turbo frame swap can't leave the panel
    // describing the state the previous render was in.
    summaryLineTargetConnected() {
        this.refreshSummary();
    }

    // Recomputes the whole panel from the controls' current state rather than reacting to one
    // checkbox at a time - that's what keeps the server-rendered first paint and the client in
    // agreement. Every line's copy is already in the DOM; this only ever toggles `hidden`.
    refreshSummary() {
        const premiere = this.hasPremiereToggleTarget && this.premiereToggleTarget.checked;
        const scheduled = this.hasScheduleRadioTarget && this.scheduleRadioTarget.checked;
        const notify = this.hasNotifySelectTarget ? this.notifySelectTarget.value : null;

        const untilPublish = this.secondsUntilPublish();
        const minLead = Number(this.element.dataset.minLeadSeconds);
        const tooSoon = untilPublish !== null && untilPublish <= minLead;

        // A lead only means anything for a scheduled premiere still outside the floor. Disabled
        // as well as hidden so the control can never submit a stale value the creator can't see.
        const leadApplies = premiere && scheduled && !tooSoon;
        if (this.hasNotifyFieldTarget) this.notifyFieldTarget.classList.toggle("hidden", !leadApplies);
        if (this.hasNotifySelectTarget) this.notifySelectTarget.disabled = !leadApplies;
        if (this.hasNotifyImmediateNoteTarget) {
            this.notifyImmediateNoteTarget.classList.toggle("hidden", !(premiere && !scheduled));
        }
        if (this.hasNotifyTooSoonNoteTarget) {
            this.notifyTooSoonNoteTarget.classList.toggle("hidden", !(premiere && scheduled && tooSoon));
        }

        if (!this.hasSummaryLineTarget && !this.hasSummaryEmptyTarget) return;

        const state = {
            premiere,
            email: this.hasEmailToggleTarget && this.emailToggleTarget.checked,
            discord: this.hasDiscordToggleTarget && this.discordToggleTarget.checked,
            notify,
            reminder: leadApplies && notify !== null && notify !== "off",
            // Number("off") is NaN, and NaN >= x is false, so "Don't notify" is naturally excluded.
            firesNow: untilPublish !== null && Number(notify) >= untilPublish,
            tooSoon,
        };

        let announced = false;
        for (const line of this.summaryLineTargets) {
            const shown = this.summaryLineShown(line.dataset, state);
            line.classList.toggle("hidden", !shown);
            announced = announced || shown;
        }

        // The caveat and the too-soon line both ride with the reminder rather than counting as a
        // notification themselves, so neither ever keeps the "nothing sends" line off screen.
        if (this.hasSummaryCaveatTarget) this.summaryCaveatTarget.classList.toggle("hidden", !(state.reminder && !state.firesNow));
        if (this.hasSummaryTooSoonTarget) {
            this.summaryTooSoonTarget.classList.toggle("hidden", !(premiere && scheduled && state.tooSoon && notify !== null && notify !== "off"));
        }
        if (this.hasSummaryEmptyTarget) this.summaryEmptyTarget.classList.toggle("hidden", announced);
    }

    summaryLineShown(dataset, state) {
        switch (dataset.summaryLine) {
            case "reminder":            return state.reminder && dataset.summaryLead === state.notify && !state.firesNow;
            case "reminder_fires_now":  return state.reminder && dataset.summaryLead === state.notify && state.firesNow;
            case "publish_email_premiere": return state.email && state.premiere;
            case "publish_email_plain": return state.email && !state.premiere;
            case "discord": return state.discord;
            default: return false;
        }
    }
}

FormController.targets = [
    "playlistSelect",
    "emailContent",
    "emailContentAlert",
    "premiereBanner",
    "premiereToggle",
    "scheduleRadio",
    "emailToggle",
    "discordToggle",
    "notifyField",
    "notifySelect",
    "notifyImmediateNote",
    "notifyTooSoonNote",
    "publishAtInput",
    "summaryLine",
    "summaryEmpty",
    "summaryCaveat",
    "summaryTooSoon",
]
